import React, { useState, useEffect } from "react";
import { addRoom, getRoomById, getRoomTypes, updateRoom } from "../../apis/room";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

type AddRoomProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  roomId?: number;
};

interface RoomType {
  id: number;
  name: string;
}

interface BedRow {
  id: string;
  name: string;
  type: string;
}

type RoomErrors = {
  name?: string;
  floor?: string;
  room_type_id?: string;
  beds?: string;
};

const defaultBeds = (): BedRow[] => [{ id: crypto.randomUUID(), name: "Bed 1", type: "" }];

const AddRoom: React.FC<AddRoomProps> = ({ open, onClose, onSuccess, roomId }) => {
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
    room_type_id: "",
    status: "available",
  });
  const [beds, setBeds] = useState<BedRow[]>(defaultBeds);

  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [errors, setErrors] = useState<RoomErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRoom, setLoadingRoom] = useState(false);

  const isEditMode = Boolean(roomId);

  useEffect(() => {
    if (open) fetchRoomTypes();
  }, [open]);

  useEffect(() => {
    if (open && roomId) {
      setLoadingRoom(true);
      getRoomById(roomId)
        .then((room: any) => {
          const data = room?.data ?? room;
          setFormData({
            name: data.name ?? "",
            floor: String(data.floor ?? ""),
            room_type_id: String(data.roomType?.id ?? data.room_type?.id ?? ""),
            status: data.status ?? "available",
          });
          const bedsList = data.beds ?? data?.data?.beds ?? [];
          if (Array.isArray(bedsList) && bedsList.length > 0) {
            setBeds(
              bedsList.map((b: any) => ({
                id: String(b.id ?? crypto.randomUUID()),
                name: b.name ?? "Bed 1",
                type: b.type ?? "",
              }))
            );
          } else {
            setBeds(defaultBeds());
          }
        })
        .catch((err) => {
          console.error("Failed to fetch room", err);
          setErrors({ name: "Failed to load room details" });
        })
        .finally(() => setLoadingRoom(false));
    } else if (open && !roomId) {
      setFormData({ name: "", floor: "", room_type_id: "", status: "available" });
      setBeds(defaultBeds());
      setErrors({});
    }
  }, [open, roomId]);

  const fetchRoomTypes = async () => {
    try {
      const res: any = await getRoomTypes();
      setRoomTypes(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch room types", err);
    }
  };

  if (!open) return null;

  if (loadingRoom) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-8 text-center">
          <p className="text-gray-600">Loading room details...</p>
        </div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name as keyof RoomErrors]: "" }));
  };

  const validateForm = () => {
    const e: RoomErrors = {};
    if (!formData.name.trim()) e.name = "* Room name is required";
    if (!formData.floor) e.floor = "* Floor is required";
    if (!formData.room_type_id) e.room_type_id = "* Room type is required";
    const bedsWithNames = beds.filter((b) => b.name.trim());
    if (bedsWithNames.length === 0) e.beds = "* At least one bed with a name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const addBed = () => {
    setBeds((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: `Bed ${prev.length + 1}`, type: "" },
    ]);
  };

  const removeBed = (id: string) => {
    setBeds((prev) => (prev.length > 1 ? prev.filter((b) => b.id !== id) : prev));
  };

  const updateBed = (id: string, field: "name" | "type", value: string) => {
    setBeds((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const payload = {
      name: formData.name.trim(),
      floor: Number(formData.floor),
      room_type_id: Number(formData.room_type_id),
      status: formData.status,
      beds: beds.map((b) => ({
        name: b.name.trim() || `Bed ${beds.indexOf(b) + 1}`,
        type: b.type.trim() || undefined,
      })),
    };
    try {
      if (roomId) {
        await updateRoom(roomId, payload);
      } else {
        await addRoom(payload);
      }

      handleClose();
      onSuccess?.();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const apiErrors: RoomErrors = {};
        Object.entries(err.response.data.errors).forEach(([key, val]: any) => {
          apiErrors[key as keyof RoomErrors] = val[0];
        });
        setErrors(apiErrors);
      } else {
        setErrors({ name: isEditMode ? "Failed to update room" : "Failed to add room" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", floor: "", room_type_id: "", status: "available" });
    setBeds(defaultBeds());
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary rounded-t-lg">
          <h2 className="text-white text-base font-semibold">{isEditMode ? "Edit Room" : "Add Room"}</h2>
          <button
            onClick={handleClose}
            type="button"
            className="text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Room Name */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Room Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
          </div>

          {/* Floor */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Floor</label>
            <input
              type="number"
              name="floor"
              value={formData.floor}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            {errors.floor && <span className="text-sm text-red-500">{errors.floor}</span>}
          </div>

          {/* Room Type */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Room Type</label>
            <select
              name="room_type_id"
              value={formData.room_type_id}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="">Select Room Type</option>
              {roomTypes.map((type) => (
                <option key={type.id} value={type.id.toString()}>{type.name}</option>
              ))}
            </select>
            {errors.room_type_id && <span className="text-sm text-red-500">{errors.room_type_id}</span>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          {/* Beds */}
          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-900">Beds</label>
              <button
                type="button"
                onClick={addBed}
                className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" />
                Add bed
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Each room has at least one bed. Add more and optionally set a type (e.g. Manual, Electric).</p>
            {errors.beds && <span className="text-sm text-red-500 block mb-2">{errors.beds}</span>}
            <ul className="space-y-3">
              {beds.map((bed) => (
                <li
                  key={bed.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex-1 min-w-[120px]">
                    <label className="sr-only">Bed name</label>
                    <input
                      type="text"
                      value={bed.name}
                      onChange={(e) => updateBed(bed.id, "name", e.target.value)}
                      placeholder="e.g. Bed 1"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="sr-only">Type</label>
                    <input
                      type="text"
                      value={bed.type}
                      onChange={(e) => updateBed(bed.id, "type", e.target.value)}
                      placeholder="e.g. Manual, Electric (optional)"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeBed(bed.id)}
                    disabled={beds.length === 1}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:pointer-events-none"
                    title="Remove bed"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-2 bg-primary hover:bg-hover text-white rounded disabled:opacity-50"
            >
              {isSubmitting ? (isEditMode ? "Updating..." : "Saving...") : isEditMode ? "Update" : "Save"}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoom;